import { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";

import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";
import { useProductAllListData } from "../../../apis/promoApis";
// 👇 Import the new Row Component
import ShipmentItemRow from "./ShipmentItemRow";

// Define proper types for better TypeScript safety
type ProductItem = {
  pId: string;
  quantity: string;
  weight: number;
};

interface ShipmentItemSectionProps {
  control: any; // from react-hook-form
  setValue: any;
  watch?: any;
  register: any;
  errors: any;
  isSidebar?: boolean;
}

export default function ShipmentItemSection({
  control,
  setValue,
  register,
  errors,
  isSidebar = false,
}: ShipmentItemSectionProps) {
  const { data: products } = useProductAllListData();

  // ✅ SAFE: Calling useWatch at the top level of the component
  // Watch the entire products array (stable reference)
  const productsArray = useWatch({
    control,
    name: "products",
  }) as ProductItem[] | undefined;

  // ✅ SAFE: Calling useWatch at the top level of the component
  // Watch last row fields (for enabling the "Add" button)
  const lastRowIndex = (productsArray?.length ?? 1) - 1;
  const lastRowPId = useWatch({
    control,
    name: `products[${lastRowIndex}].pId`,
  }) as string | undefined;

  const lastRowQuantity = useWatch({
    control,
    name: `products[${lastRowIndex}].quantity`,
  }) as string | undefined;

  // Rows are only used for stable keys
  const [rows, setRows] = useState<{ id: number }[]>([{ id: Date.now() }]);

  // Sync rows with products length
  useEffect(() => {
    const current = productsArray || [];

    if (current.length === 0) {
      setValue("products", [{ pId: "", quantity: "", weight: 0 }], {
        shouldValidate: true,
      });
      setRows([{ id: Date.now() }]);
      return;
    }

    // Only update rows when length changes
    if (current.length !== rows.length) {
       // Ensure stable keys by preserving existing IDs when possible
       const newRows = current.map((_, i) => ({
        id: rows[i]?.id ?? Date.now() + i,
      }));
      setRows(newRows);
    }
  }, [productsArray?.length, setValue, rows.length]);

  const addRow = () => {
    setRows((prev) => [...prev, { id: Date.now() }]);
    const current = productsArray || [];
    setValue("products", [...current, { pId: "", quantity: "", weight: 0 }], {
      shouldValidate: true,
    });
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    const current = productsArray || [];
    const updated = current.filter((_, i) => i !== index);
    setValue("products", updated, { shouldValidate: true });
  };

  // Auto-fill weight when a product is selected
  useEffect(() => {
    if (!productsArray || !products) return;

    productsArray.forEach((item, index) => {
      if (!item.pId) return;

      const selectedProduct = products.find((p: any) => p.id === item.pId);
      if (selectedProduct && selectedProduct.weight !== item.weight) {
        setValue(`products[${index}].weight`, selectedProduct.weight, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    });
  }, [productsArray, products, setValue]);

  // Auto-calculate totals
  useEffect(() => {
    if (!productsArray) return;

    const totals = productsArray.reduce(
      (acc, p) => {
        const qty = Number(p.quantity) || 0;
        const wt = Number(p.weight) || 0;
        acc.qty += qty;
        acc.wt += qty * wt;
        return acc;
      },
      { qty: 0, wt: 0 }
    );

    setValue("shipment.numberOfPieces", totals.qty, { shouldValidate: true });
    setValue("shipment.totalWeight", totals.wt, { shouldValidate: true });
  }, [productsArray, setValue]);

  return (
    <div className={isSidebar ? "flex flex-col gap-3" : "grid grid-cols-2 gap-4"}>
      {rows.map((row, index) => {
        return (
          <ShipmentItemRow
            key={row.id}
            index={index}
            control={control}
            setValue={setValue}
            register={register}
            errors={errors}
            products={products}
            onRemove={() => removeRow(index)}
            onAdd={addRow}
            isLastRow={index === rows.length - 1}
            lastRowQuantity={lastRowQuantity}
            isSidebar={isSidebar}
          />
        );
      })}
    </div>
  );
}